import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./header.css";
import { FaSearch } from "react-icons/fa";
import { Heart, ShoppingCart, User, X, Menu, ChevronDown } from "lucide-react";
import {
  fetchProfileDashboard,
  updateProfile,
} from "../../api/profile.api";
import mainLogo from "../../images/mainlogo.jpg";
import { useCart } from "../../context/CartContext";
import { useFavorites } from "../../context/FavoritesContext";
import { useAuth } from "../../context/AuthContext";
import { fetchBrands } from "../../api/brands.api";
import { sortBrandsForDisplay } from "../../utils/brandDisplayOrder";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

const resolveCategoryImage = (category = {}) => {
  const image =
    category.image_small ||
    category.image_url ||
    category.image ||
    category.logo ||
    "";

  if (!image) return "";
  if (/^(https?:)?\/\//i.test(image) || /^data:/i.test(image) || /^blob:/i.test(image)) {
    return image;
  }

  return `${API_BASE_URL}${image.startsWith("/") ? image : `/${image}`}`;
};

const HeaderNav = ({ onCartClick, onFavoritesClick, onUserClick }) => {
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const [profileData, setProfileData] = useState(null);
  const { cartCount } = useCart();
  const { favoritesCount } = useFavorites();
  const { isAuthenticated, signOut } = useAuth();
  const [loading, setLoading] = useState();
  const [error, setError] = useState();

  const [query, setQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  /* -------------------- Fetch Brands For Browse Categories -------------------- */
  useEffect(() => {
    let mounted = true;
    setLoadingCategories(true);

    fetchBrands()
      .then((data) => {
        if (!mounted) return;
        setCategories(sortBrandsForDisplay(Array.isArray(data) ? data : []));
      })
      .catch(() => {
        if (mounted) setCategories([]);
      })
      .finally(() => {
        if (mounted) setLoadingCategories(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const nav = document.querySelector(".nav-links");
    if (!nav) return;

    const onWheel = (e) => {

      if (nav.scrollWidth <= nav.clientWidth) return;

      e.preventDefault();
      nav.scrollLeft += e.deltaY;
    };

    nav.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      nav.removeEventListener("wheel", onWheel);
    };
  }, []);


  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!menuRef.current?.contains(e.target)) {
        setMobileMenuOpen(false);
        setCategoriesOpen(false);
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);
  useEffect(() => {
    if (!isAuthenticated || !showUserMenu) return;

    setLoading(true);
    setError(null);

    fetchProfileDashboard()
      .then((data) => {
        setProfileData(data);
      })
      .catch(() => {
        setError("Unable to load profile data");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isAuthenticated, showUserMenu]);


  const handleNavigate = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
    setCategoriesOpen(false);
    setShowUserMenu(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    navigate(`/search?q=${query.trim()}`);
  };

  const handleUserIconClick = () => {
    
    if (!isAuthenticated) {
      onUserClick(); 
      return;
    }

    setShowUserMenu((prev) => !prev);
  };



  const handleProfileClick = () => {
    setShowUserMenu(false);
    navigate("/profile");
  };

  const handleSignOut = async () => {
    await signOut();
    setShowUserMenu(false);
    setProfileData(null);
    setError(null);
    setLoading(false);

    navigate("/");
  };

  const {
    personal_info,
    membership,
    points_balance,
    recent_orders,
    wishlist_items,
    default_address,
  } = profileData || {};


  return (
    <header className="header" ref={menuRef}>
      {/* ================= TOP BAR ================= */}
      <div className="header-top">
        <div className="logo" onClick={() => navigate("/")}>
          <img src={mainLogo} alt="Diecast Store" />
        </div>

        <form className="search-box" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Search products or brands"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="submit" className="search-btn">
            <FaSearch />
          </button>
        </form>

        <div className="header-icons">
          <div
            className="icon-item favorites-icon-wrapper"
            onClick={onFavoritesClick}
          >
            <span className="icon-holder">
              <Heart className="icon" />
              {favoritesCount > 0 && (
                <span className="badge">{favoritesCount}</span>
              )}
            </span>
          </div>

          <div className="icon-item user-icon-wrapper" onClick={handleUserIconClick}>
            <User className="icon" />

            {isAuthenticated && showUserMenu && (
              <div className="user-popover" onClick={(e) => e.stopPropagation()}>
                <div className="modal-container">
                  <button className="close-btn" onClick={() => setShowUserMenu(false)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>

                  {loading && (
                    <div className="menu-loading">
                      <div className="spinner"></div>
                      <p>Loading profile...</p>
                    </div>
                  )}

                  {!loading && error && (
                    <div className="menu-error">
                      <p>{error}</p>
                      <button onClick={handleUserIconClick}>Retry</button>
                    </div>
                  )}

                  <div className="modal-header">
                    <img
                      src="https://api.dicebear.com/9.x/avataaars/svg?seed=shancrazy"
                      alt="User Avatar"
                      className="avatar"
                    />
                    <div className="user-info">
                      <h3 className="username">
                        {personal_info?.last_name
                          ? `${personal_info.first_name} ${personal_info.last_name}`
                          : personal_info?.first_name || "User"}
                      </h3>

                      <span className="membership">{membership ? membership.type : "Free"}</span>
                    </div>
                  </div>

                  <div className="menu-list">
                    <div className="menu-item" onClick={handleProfileClick}>
                      <span className="icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                      </span>
                      <span>PROFILE</span>
                    </div>

                    <div className="menu-item" onClick={handleSignOut}>
                      <span className="icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                          <polyline points="16 17 21 12 16 7"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12"></line>
                        </svg>
                      </span>
                      <span>LOGOUT</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            className="icon-item cart-icon-wrapper"
            onClick={onCartClick}
          >
            <span className="icon-holder">
              <ShoppingCart className="icon" />
              {cartCount > 0 && (
                <span className="badge">{cartCount}</span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* ================= NAV ================= */}
      <nav className="navbar">
        {/* DESKTOP NAV (controlled by CSS) */}
        <div className="desktop-nav">
          <div className="category-wrapper">
            <button
              className="category-btn"
              type="button"
              onClick={() => setCategoriesOpen((p) => !p)}
              aria-expanded={categoriesOpen}
              aria-haspopup="true"
            >
              {categoriesOpen ? (
                <X size={20} className="menu-icon" />
              ) : (
                <Menu size={20} className="menu-icon" />
              )}
              BROWSE CATEGORIES
            </button>

            {categoriesOpen && (
              <div className="dropdown-menu">
                {loadingCategories && (
                  <div className="dropdown-item">Loading...</div>
                )}

                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    className="dropdown-item"
                    onClick={() =>
                      handleNavigate(`/category/${encodeURIComponent(cat.name)}`)
                    }
                  >
                    <img src={resolveCategoryImage(cat)} alt={cat.name} />
                    <span>{cat.name.toUpperCase()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <ul className="nav-links">
            <li onClick={() => handleNavigate("/")}>HOME</li>
            <li onClick={() => handleNavigate("/about-us")}>ABOUT US</li>
            <li onClick={() => handleNavigate("/pre-order")}>PRE ORDER</li>
            <li onClick={() => handleNavigate("/event-exclusive")}>Chase & Event Exclusive</li>
            <li onClick={() => handleNavigate("/membership")}>
              MEMBERSHIP
            </li>
            <li onClick={() => handleNavigate("/Redeempage")}>REDEEM</li>
          </ul>

          <button
            className="vendor-btn"
            onClick={() => handleNavigate("/vendor-partner")}
          >
            VENDOR PARTNER
          </button>
        </div>

        {/* MOBILE MENU BUTTON */}
        <button
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(true)}
        >
          <Menu /> Menu
        </button>

        {/* MOBILE MENU */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            <div className="mobile-header">
              <img src={mainLogo} alt="Logo" className="logo" />
              <button className="close-btn" onClick={() => setMobileMenuOpen(false)}>
                <X size={28} />
              </button>
            </div>


            {/* <div className="mobile-section">
              <button
                className="mobile-section-btn"
                onClick={() => setCategoriesOpen((p) => !p)}
              >
                Categories
                <ChevronDown
                  size={18}
                  className={`chevron ${categoriesOpen ? "rotate" : ""
                    }`}
                />
              </button>

              {categoriesOpen && (
                <div className="mobile-dropdown">
                  {loadingCategories && <div>Loading...</div>}
                  {categories.map((cat) => (
                    <div
                      key={cat.id}
                      className="dropdown-item"
                      onClick={() =>
                        handleNavigate(`/category/${cat.slug}`)
                      }
                    >
                      <img src={cat.image} alt={cat.name} />
                      {cat.name}
                    </div>
                  ))}
                </div>
              )}
            </div> */}

            <ul className="mobile-nav-links">
              <li onClick={() => handleNavigate("/")}>HOME</li>
              <li onClick={() => handleNavigate("/pre-order")}>PRE ORDER</li>

              <li onClick={() => handleNavigate("/membership")}>
                MEMBERSHIP
              </li>
              <li onClick={() => handleNavigate("/Redeempage")}>REDEEM</li>
            <li onClick={() => handleNavigate("/event-exclusive")}>
              CHASE & EVENT EXCLUSIVE
            </li>
              <li onClick={() => handleNavigate("/vendor-partner")}>
                VENDOR PARTNER
              </li>

              {/* Categories */}
              <li
                className="has-dropdown"
                onClick={() => setCategoriesOpen((p) => !p)}
              >
                <span>CATEGORIES</span>
                <ChevronDown
                  size={18}
                  className={`chevron ${categoriesOpen ? "rotate" : ""}`}
                />
              </li>

              {categoriesOpen && (
                <ul className="mobile-dropdown">
                  {loadingCategories && (
                    <li className="dropdown-item">Loading...</li>
                  )}

                  {categories.map((cat) => (
                    <li
                      key={cat.id}
                      className="dropdown-item"
                      onClick={() =>
                        handleNavigate(
                          `/category/${encodeURIComponent(cat.name)}`
                        )
                      }
                    >
                      <img src={resolveCategoryImage(cat)} alt={cat.name} />
                      {cat.name}
                    </li>
                  ))}
                </ul>
              )}
            </ul>
          </div>
        )}
      </nav>
    </header>
  );
};

export default HeaderNav;
